import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Loader2, Save, RefreshCw, AlertTriangle,
  SlidersHorizontal, ListChecks, Eye, EyeOff, Info, Building2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useOrg } from '@/contexts/OrgContext'
import { useCadence } from '@/contexts/CadenceContext'
import { supabase } from '@/integrations/supabase/client'
import { updateQaSettings } from '@/lib/edge-functions'
import { StepRubricTable, type StepRubricRow } from '@/components/qa/StepRubricTable'
import { CarlosExplainer } from '@/components/autom-cadence/CarlosExplainer'
import { stepNeedsRubric } from '@/lib/cadence-step-predicates'

// The rubric table follows stepNeedsRubric (shared helper): every message-
// generating step EXCEPT linkedin_comment gets a rubric row. linkedin_comment is
// excluded because Carlos validates comments with rule-based structural bans (not
// a numeric rubric) and the qa-settings-update edge fn REJECTS persisting a
// comment rubric — a comment row here would be a dead card that can never be
// saved. A plain Day-0 invite (send_note !== true) generates no message, so it
// gets no rubric row either. The Day-9 `task` step is covered because it is one
// of the always-message types the shared helper recognizes as message-generating.

// ── QA globals shape (subset of org_chief_settings.qa_*) ────────────────────
interface QaGlobals {
  qa_threshold: number
  qa_max_attempts: number
  qa_min_acceptable_score: number
  qa_dead_band_width: number
  qa_shadow_mode_active: boolean
}

interface GlobalsForm {
  qa_threshold: string
  qa_max_attempts: string
  qa_min_acceptable_score: string
  qa_dead_band_width: string
  qa_shadow_mode_active: boolean
}

function toForm(g: QaGlobals): GlobalsForm {
  return {
    qa_threshold: String(g.qa_threshold),
    qa_max_attempts: String(g.qa_max_attempts),
    qa_min_acceptable_score: String(g.qa_min_acceptable_score),
    qa_dead_band_width: String(g.qa_dead_band_width),
    qa_shadow_mode_active: g.qa_shadow_mode_active,
  }
}

// Raw row shape read from carlos_step_rubric (now with cadence_id per mig 172).
interface RawRubricRow extends StepRubricRow {
  cadence_id: string | null
}

// The rubric-relevant steps of THIS cadence: (step_type, day_offset) pairs that
// Carlos reviews, deduped and day-sorted. This is what drives the table — the
// REAL steps of the cadence, NOT whatever rows happen to exist in the table.
interface RubricStepKey {
  step_type: string
  day_offset: number
}

// Build the EFFECTIVE rubric row for each REAL rubric step of this cadence.
// For every (step_type, day_offset) Carlos reviews we look up the effective row
// via the runtime cascade (cadence-scoped > org-global cadence_id IS NULL). If
// one exists we surface it (carrying its provenance); if none exists we emit a
// SYNTHETIC empty row seeded from the org-global qa_* defaults so the user can
// create a cadence-scoped rubric for that real step. Mirrors AnglesSection.
function buildEffectiveRubrics(
  stepKeys: RubricStepKey[],
  rows: RawRubricRow[],
  cadenceId: string,
  orgId: string,
  globals: QaGlobals | null,
): { rows: StepRubricRow[]; usesOrgGlobalFallback: boolean } {
  // Effective row per (step_type, day_offset): cadence-scoped wins over org-global.
  const byKey = new Map<string, RawRubricRow>()
  for (const r of rows) {
    if (r.cadence_id !== null && r.cadence_id !== cadenceId) continue // another cadence — ignore
    const key = `${r.step_type}::${r.day_offset}`
    const existing = byKey.get(key)
    if (!existing || (existing.cadence_id === null && r.cadence_id === cadenceId)) {
      byKey.set(key, r)
    }
  }

  const out: StepRubricRow[] = stepKeys.map(({ step_type, day_offset }) => {
    const key = `${step_type}::${day_offset}`
    const found = byKey.get(key)
    if (found) {
      return {
        id: found.id,
        org_id: found.org_id,
        step_type: found.step_type,
        day_offset: found.day_offset,
        threshold: found.threshold,
        min_acceptable: found.min_acceptable,
        max_attempts: found.max_attempts,
        system_prompt_addendum: found.system_prompt_addendum,
        cadence_id: found.cadence_id,
        // Carry the pass-through JSONB so editing an inherited row forks a
        // cadence-scoped copy WITH the risk_trigger filters intact.
        rubric_focus: found.rubric_focus ?? null,
        rubric_skip: found.rubric_skip ?? null,
      }
    }
    // No effective row — synthesize an empty one seeded from the org-global
    // qa_* defaults. cadence_id is left `undefined` so the table shows no
    // provenance badge (nothing is inherited yet, nothing is saved yet); saving
    // it forks a cadence-scoped row keyed by (step_type, day_offset).
    return {
      id: `empty-${step_type}-${day_offset}`,
      org_id: orgId,
      step_type,
      day_offset,
      threshold: globals?.qa_threshold ?? 7,
      min_acceptable: globals?.qa_min_acceptable_score ?? 4.5,
      max_attempts: globals?.qa_max_attempts ?? 5,
      system_prompt_addendum: null,
      cadence_id: undefined,
      // No effective row exists → nothing to inherit. Leave undefined so on save
      // StepRubricTable sends undefined and the backend seeds defaults.
      rubric_focus: undefined,
      rubric_skip: undefined,
    }
  })

  const usesOrgGlobalFallback = out.some((r) => r.cadence_id === null)
  return { rows: out, usesOrgGlobalFallback }
}

// ────────────────────────────────────────────────────────────────────────────
// PolicySection — the "Política" tab of Autom Cadence. QA globals stay org-wide
// (labeled "aplica a toda la org"); per-step rubrics are read scoped to THIS
// cadence with a fallback to the org-global (cadence_id IS NULL) legacy rows so
// the effective config Carlos resolves to is what the user sees.
// ────────────────────────────────────────────────────────────────────────────
export function PolicySection({ cadenceId }: { cadenceId: string }) {
  const { orgId } = useOrg()
  const { cadences } = useCadence()
  const queryClient = useQueryClient()

  // ── The REAL rubric steps of THIS cadence (derived from cadence.steps, exactly
  // like AnglesSection derives its cards from the real message-step days). This is
  // what the table renders — NOT whatever rows happen to live in the rubric table.
  const cadence = cadences.find((c) => c.id === cadenceId) ?? null
  const stepKeys = useMemo<RubricStepKey[]>(() => {
    const byKey = new Map<string, RubricStepKey>()
    for (const s of cadence?.steps ?? []) {
      if (!stepNeedsRubric(s)) continue
      const key = `${s.step_type}::${s.day_offset}`
      if (!byKey.has(key)) byKey.set(key, { step_type: s.step_type, day_offset: s.day_offset })
    }
    return [...byKey.values()].sort((a, b) => {
      if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
      return a.step_type.localeCompare(b.step_type)
    })
  }, [cadence])

  // ── Read QA globals (org_chief_settings has RLS: org members can SELECT) ────
  const globalsQuery = useQuery({
    queryKey: ['qa-globals', orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<QaGlobals | null> => {
      const { data, error } = await supabase
        .from('org_chief_settings')
        .select('qa_threshold, qa_max_attempts, qa_min_acceptable_score, qa_dead_band_width, qa_shadow_mode_active')
        .eq('org_id', orgId)
        .maybeSingle()
      if (error) throw new Error(error.message)
      if (!data) return null
      return {
        qa_threshold: Number(data.qa_threshold),
        qa_max_attempts: Number(data.qa_max_attempts),
        qa_min_acceptable_score: Number(data.qa_min_acceptable_score),
        qa_dead_band_width: Number(data.qa_dead_band_width),
        qa_shadow_mode_active: Boolean(data.qa_shadow_mode_active),
      }
    },
  })

  // ── Read per-step rubrics for THIS cadence + the org-global fallback rows ────
  // We read both the cadence-scoped rows and the org-global (cadence_id IS NULL)
  // rows, then merge to the effective config. The query key includes cadenceId so
  // switching cadences refetches.
  const rubricsQuery = useQuery({
    queryKey: ['qa-step-rubrics', orgId, cadenceId],
    enabled: !!orgId && !!cadenceId,
    queryFn: async (): Promise<RawRubricRow[]> => {
      const { data, error } = await supabase
        .from('carlos_step_rubric')
        .select('id, org_id, cadence_id, step_type, day_offset, threshold, min_acceptable, max_attempts, system_prompt_addendum, rubric_focus, rubric_skip')
        .eq('org_id', orgId)
        .or(`cadence_id.eq.${cadenceId},cadence_id.is.null`)
        .order('day_offset', { ascending: true })
        .order('step_type', { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []).map((r) => ({
        id: r.id as string,
        org_id: r.org_id as string,
        cadence_id: (r.cadence_id as string | null) ?? null,
        step_type: r.step_type as string,
        day_offset: Number(r.day_offset),
        threshold: Number(r.threshold),
        min_acceptable: Number(r.min_acceptable),
        max_attempts: Number(r.max_attempts),
        system_prompt_addendum: (r.system_prompt_addendum as string | null) ?? null,
        // Pass-through JSONB (not editable in the UI) — carried so forking an
        // inherited rubric on save preserves the risk_trigger filters.
        rubric_focus: (r.rubric_focus as unknown) ?? null,
        rubric_skip: (r.rubric_skip as unknown) ?? null,
      }))
    },
  })

  // ── Globals form state (hydrated from query; adjust-during-render) ──────────
  const [form, setForm] = useState<GlobalsForm | null>(null)
  const [hydratedFrom, setHydratedFrom] = useState<QaGlobals | null>(null)
  if (globalsQuery.data && globalsQuery.data !== hydratedFrom) {
    setHydratedFrom(globalsQuery.data)
    setForm(toForm(globalsQuery.data))
  }

  const globalsDirty = !!form && !!globalsQuery.data &&
    JSON.stringify(form) !== JSON.stringify(toForm(globalsQuery.data))

  const saveGlobals = useMutation({
    mutationFn: async () => {
      if (!form || !orgId) throw new Error('Sin datos que guardar')
      const threshold = Number(form.qa_threshold)
      const maxAttempts = Number(form.qa_max_attempts)
      const minScore = Number(form.qa_min_acceptable_score)
      const deadBand = Number(form.qa_dead_band_width)

      if (Number.isNaN(threshold) || threshold < 0 || threshold > 10) {
        throw new Error('El umbral global debe estar entre 0 y 10')
      }
      if (!Number.isInteger(maxAttempts) || maxAttempts < 1 || maxAttempts > 10) {
        throw new Error('El max de reintentos debe ser un entero entre 1 y 10')
      }
      if (Number.isNaN(minScore) || minScore < 0 || minScore > 10) {
        throw new Error('El piso de envío debe estar entre 0 y 10')
      }
      if (Number.isNaN(deadBand) || deadBand < 0 || deadBand > 5) {
        throw new Error('La banda muerta debe estar entre 0 y 5')
      }
      if (minScore > threshold) {
        throw new Error('El piso de envío no puede ser mayor que el umbral')
      }
      return updateQaSettings({
        operation: 'globals',
        orgId,
        qa_threshold: threshold,
        qa_max_attempts: maxAttempts,
        qa_min_acceptable_score: minScore,
        qa_dead_band_width: deadBand,
        qa_shadow_mode_active: form.qa_shadow_mode_active,
      })
    },
    onSuccess: () => {
      toast.success('Globales de QA guardados')
      queryClient.invalidateQueries({ queryKey: ['qa-globals', orgId] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'No se pudieron guardar los globales')
    },
  })

  const loading = globalsQuery.isLoading || rubricsQuery.isLoading
  const loadError = globalsQuery.error || rubricsQuery.error

  const merged = buildEffectiveRubrics(
    stepKeys,
    rubricsQuery.data ?? [],
    cadenceId,
    orgId ?? '',
    globalsQuery.data ?? null,
  )

  return (
    <div className="space-y-6">
      {/* Header row (section-level refresh) */}
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Aquí ajustas cuán exigente es Carlos, el validador que revisa cada mensaje antes de
          enviarlo. Los <strong>globales</strong> aplican a toda la organización; las
          <strong> rúbricas por paso</strong> son específicas de esta cadencia y mandan sobre el
          umbral global para ese paso.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { globalsQuery.refetch(); rubricsQuery.refetch() }}
          disabled={globalsQuery.isFetching || rubricsQuery.isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${(globalsQuery.isFetching || rubricsQuery.isFetching) ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando política de QA…
        </CardContent></Card>
      ) : loadError ? (
        <Card className="border-red-200 dark:border-red-900"><CardContent className="py-8 text-center text-sm">
          <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> No se pudo leer la política de QA.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => { globalsQuery.refetch(); rubricsQuery.refetch() }}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reintentar
          </Button>
        </CardContent></Card>
      ) : (
        <>
          {/* ── How Carlos scores (fixed explainer, real org numbers) ──────── */}
          <CarlosExplainer
            threshold={globalsQuery.data?.qa_threshold ?? null}
            maxAttempts={globalsQuery.data?.qa_max_attempts ?? null}
            minAcceptable={globalsQuery.data?.qa_min_acceptable_score ?? null}
          />

          {/* ── Globals (org-wide) ─────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <SlidersHorizontal className="h-5 w-5" />
                Globales de QA
                <Badge variant="secondary" className="ml-1 gap-1 font-normal">
                  <Building2 className="h-3 w-3" /> aplica a toda la org
                </Badge>
              </CardTitle>
              <CardDescription>
                Valores por defecto para toda la organización. Si un paso tiene su propia
                rúbrica (abajo), esa manda sobre el umbral global para ese paso.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {!form ? (
                <p className="text-sm text-muted-foreground">
                  Esta organización aún no tiene ajustes de QA guardados. Guarda una vez para crearlos.
                </p>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <GlobalNumberField
                  label="Umbral global de aprobación"
                  hint="Puntaje mínimo (0–10) para aprobar y enviar un mensaje. Más alto = más exigente."
                  value={form?.qa_threshold ?? ''}
                  step="0.1" min={0} max={10}
                  onChange={(v) => setForm((s) => s && { ...s, qa_threshold: v })}
                />
                <GlobalNumberField
                  label="Máx. reintentos (regeneraciones)"
                  hint="Cuántas veces Carlos pide regenerar antes de rendirse y mandar el mejor intento."
                  value={form?.qa_max_attempts ?? ''}
                  step="1" min={1} max={10}
                  onChange={(v) => setForm((s) => s && { ...s, qa_max_attempts: v })}
                />
                <GlobalNumberField
                  label="Piso de envío (min-score)"
                  hint="Al agotar reintentos, si el mejor intento alcanza este piso se envía; por debajo, se salta el touch para no quemar el lead."
                  value={form?.qa_min_acceptable_score ?? ''}
                  step="0.1" min={0} max={10}
                  onChange={(v) => setForm((s) => s && { ...s, qa_min_acceptable_score: v })}
                />
                <GlobalNumberField
                  label="Banda muerta (dead band)"
                  hint="Margen alrededor del umbral donde Carlos ni aprueba ni regenera automáticamente; decide por dimensiones. 0 = decisión limpia por umbral."
                  value={form?.qa_dead_band_width ?? ''}
                  step="0.1" min={0} max={5}
                  onChange={(v) => setForm((s) => s && { ...s, qa_dead_band_width: v })}
                />
              </div>

              {/* Shadow mode toggle */}
              <div className="rounded-md border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Label className="flex items-center gap-1.5 text-sm">
                      {form?.qa_shadow_mode_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      Modo sombra (shadow mode)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Con el modo sombra ENCENDIDO, Carlos evalúa y registra su decisión pero
                      <strong> no actúa</strong>: los mensajes salen sin que apruebe/rechace/regenere.
                      Úsalo para probar cambios de política sin afectar envíos. Apágalo para que
                      Carlos aplique sus decisiones en vivo.
                    </p>
                  </div>
                  <Switch
                    checked={form?.qa_shadow_mode_active ?? false}
                    onCheckedChange={(v) => setForm((s) => s && { ...s, qa_shadow_mode_active: v })}
                  />
                </div>
                {form?.qa_shadow_mode_active && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    Con el modo sombra activo, Carlos NO frena mensajes malos. Solo activa esto si
                    estás validando la política y aceptas que los mensajes salgan sin gate de calidad.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button disabled={!globalsDirty || saveGlobals.isPending || !form} onClick={() => saveGlobals.mutate()}>
                  {saveGlobals.isPending
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <Save className="h-4 w-4 mr-2" />}
                  Guardar globales
                </Button>
                {globalsDirty && (
                  <span className="text-xs text-muted-foreground">Hay cambios sin guardar</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Per-step rubrics (effective for this cadence) ──────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ListChecks className="h-5 w-5" />
                Rúbricas por paso
              </CardTitle>
              <CardDescription className="flex items-start gap-1.5">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Cada fila es un paso de la cadencia (tipo + día). Aquí afinas el umbral, el piso y
                los reintentos SOLO para ese paso, y le das a Carlos una instrucción de calidad
                específica (el textarea). Estos valores mandan sobre los globales de arriba.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {merged.rows.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  Esta cadencia no tiene pasos que Carlos revise todavía. Agrega pasos de mensaje
                  (email, DM, invitación…) o la tarea del BC en el tab Steps para poder afinar su
                  rúbrica por paso.
                </div>
              ) : (
                <>
                  {merged.usesOrgGlobalFallback && (
                    <div className="mb-3 rounded-md border border-muted bg-muted/30 p-3 flex items-start gap-2 text-xs text-muted-foreground">
                      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        Algunas rúbricas mostradas son <strong>heredadas de la configuración org-global</strong>
                        {' '}(legacy, <code>cadence_id</code> nulo) porque esta cadencia aún no tiene rúbricas
                        propias para esos pasos. Al editar y guardar una, se crea una copia propia de esta
                        cadencia. Es el comportamiento efectivo que Carlos resuelve hoy.
                      </span>
                    </div>
                  )}
                  {orgId && (
                    <StepRubricTable rows={merged.rows} orgId={orgId} cadenceId={cadenceId} />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function GlobalNumberField({
  label, hint, value, step, min, max, onChange,
}: {
  label: string
  hint: string
  value: string | number
  step: string
  min: number
  max: number
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      <Input
        type="number"
        step={step}
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9"
      />
      <p className="text-[11px] text-muted-foreground leading-tight">{hint}</p>
    </div>
  )
}
